import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import MenuList from "@material-ui/core/MenuList";
import Popper, { PopperProps } from "@material-ui/core/Popper";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import { Modifiers } from "popper.js";
import React, { useCallback, useMemo } from "react";
import { Color } from "../../theme";
import { HasTestId, PlainFunctionComponent } from "../../types";

export interface PopupMenuProps extends Omit<PopperProps, "children" | "color" | "modifiers" | "variant">, HasTestId {
    anchorEl: PopperProps["anchorEl"];
    /**
     * Menu items to be shown within the popup menu. They are wrapped into a single {@link MenuList}.
     */
    children: React.ReactNode;
    /**
     * We use `offset` modifier to override `top` and `width` of the generated `Popper` by default.
     *
     * Popper.js is based on a "plugin-like" architecture, most of its features are fully encapsulated "modifiers".
     *
     * A modifier is a function that is called each time Popper.js needs to compute the position of the popper.
     * For this reason, modifiers should be very performant to avoid bottlenecks.
     * To learn how to create a modifier, [read the modifiers documentation](https://popper.js.org/docs/v1/#modifiers).
     */
    modifiers?: Modifiers; // properly override `modifiers?: object;` from `PopperProps`
    onClose: () => void;
    /**
     * Define an offset moving the used popup menu along the x axis, e.g. in order to align an icon button and the action icons.
     * Negative `number`s move to the left, positive to the right.
     */
    xOffset?: number;
}

// override placement here because it is not possible in `muiTheme.tsx`
export const PopupMenu: PlainFunctionComponent<PopupMenuProps> = React.forwardRef(
    ({ className, children, modifiers, onClose, placement = "bottom-start", testId, xOffset, ...props }, ref) => {
        const handleMenuListClick = useCallback(onClose, [onClose]);
        const handleMenuListKeyDown = useCallback(
            (event: React.KeyboardEvent) => {
                if (event.key === "Tab") {
                    event.preventDefault();
                    onClose();
                }
            },
            [onClose],
        );

        const fixOffsetForPlacementTopAndSameWidthAsButton = useMemo(
            (): Modifiers["offset"] => ({
                enabled: true,
                fn: (data) => {
                    // move popper 1px to the top if `placement` is `top*` such that top border is not covered
                    if (data.placement.startsWith("top")) {
                        data.offsets.popper.top -= 1;
                    }
                    // for `top*` and `bottom*` placements, make popper having at least the `width` of the button
                    if (data.placement.startsWith("top") || data.placement.startsWith("bottom")) {
                        xOffset && (data.offsets.popper.left += xOffset);
                        data.styles.minWidth = `${data.offsets.reference.width}px`;
                    }
                    return data;
                },
            }),
            [xOffset],
        );

        const classes = useStyles();
        return (
            <Popper
                className={clsx(classes.menu, className)}
                data-testid={testId}
                modifiers={{ offset: fixOffsetForPlacementTopAndSameWidthAsButton, ...modifiers }}
                placement={placement}
                ref={ref}
                {...props}
            >
                <ClickAwayListener onClickAway={onClose}>
                    <MenuList className={classes.menuItemIcon} onClick={handleMenuListClick} onKeyDown={handleMenuListKeyDown}>
                        {children}
                    </MenuList>
                </ClickAwayListener>
            </Popper>
        );
    },
);

const useStyles = makeStyles(
    createStyles({
        menu: {
            backgroundColor: Color.white,
            border: `1px solid ${Color.grey2}`,
            borderRadius: "3px",
            boxShadow: "0 10px 10px 0px rgba(50, 50, 50, 0.2)", // Color.grey6
            zIndex: 501, // `zIndex` should be greater then (= above) `StickyButtonBar` -- +1 is sufficient, though
        },
        menuItemIcon: {
            "& .Mui-disabled > .MuiListItemIcon-root": {
                color: Color.grey4,
            },
        },
    }),
);
